import { addSlideFooter } from '../slide_footer';
import { LABEL_COLOR, LINE_COLOR, TEXT_COLOR, TITLE_TEXT } from '../constants';

// The base series blends with the (white) slide so only the increase/decrease/total bars show.
const BASE_COLOR = 'FFFFFF';
const INCREASE_COLOR = '00A66C';
const DECREASE_COLOR = 'E8546A';
const TOTAL_COLOR = '5F8A8F';

// Chart box (inches, default 10 x 5.625 layout).
const CHART = { h: 4.05, w: 8.9, x: 0.55, y: 1.05 };
// Estimated plot-area insets inside the chart box (axis labels). Tune here if the overlaid value
// labels drift from the bar tops — they are the only alignment-sensitive part.
const INSET = { bottom: 0.62, left: 0.72, right: 0.15, top: 0.12 };

/**
 * Builds a magnitude-scaled value-axis format code (waterfall values are often billions/millions —
 * unscaled labels are unreadable). Keeps the currency prefix from the source numbro format.
 */
const axisFormatCode = (format, max) => {
  const currency = (format || '').includes('$') ? '$' : '';
  if (max >= 1e9) return `${currency}#,##0.0,,,"B"`;
  if (max >= 1e6) return `${currency}#,##0,,"M"`;
  return `${currency}#,##0`;
};

/**
 * Creates a native (editable) waterfall slide via the stacked-bar emulation — PptxGenJS has no native
 * waterfall type. The IR's [base, increase, decrease, total] series stack so each bar lands at the
 * right height/color; the base series blends with the slide. Value labels are overlaid as text above
 * each bar (a single stacked bar can't label only the visible segments).
 *
 * @param {Array}  categories
 * @param {string} format       source numbro value format (for the axis)
 * @param {Array}  labels       per-bar display value (signed delta / total), already formatted
 * @param {Object} pptx
 * @param {Array}  series       [{ name, values }] in order: base, increase, decrease, total
 * @param {Array}  reportDateRange
 * @param {string} reportType
 * @param {string} reportUrl
 * @param {Object} shopperAttributes
 * @param {string} slideTitle
 * @param {string} title
 * @returns {Object}
 */
export const createWaterfallSlide = ({
  categories = [],
  format,
  labels = [],
  pptx,
  reportDateRange,
  reportType,
  reportUrl,
  series = [],
  shopperAttributes,
  slideTitle,
  title,
}) => {
  const slide = pptx.addSlide();
  slide.addText(slideTitle || title || '', { ...TITLE_TEXT, fontSize: 14 });

  if (!series.length || !categories.length) {
    addSlideFooter({ pptx, reportDateRange, reportType, reportUrl, shopperAttributes, slide });
    return slide;
  }

  const dataSeries = series.map((entry) => ({
    labels: categories,
    name: entry.name,
    values: entry.values,
  }));

  const stackTops = categories.map((_, index) =>
    series.reduce((sum, entry) => sum + (Number(entry.values[index]) || 0), 0)
  );
  const maxValue = Math.max(0, ...stackTops);
  const axisMax = maxValue * 1.08 || 1;

  slide.addChart(pptx.charts.BAR, dataSeries, {
    barDir: 'col',
    barGrouping: 'stacked',
    catAxisLabelColor: LABEL_COLOR,
    catAxisLabelFontSize: 8,
    catAxisLabelRotate: -20,
    catAxisLineShow: false,
    chartColors: [BASE_COLOR, INCREASE_COLOR, DECREASE_COLOR, TOTAL_COLOR],
    ...CHART,
    showLegend: false,
    showTitle: false,
    showValue: false,
    valAxisLabelColor: LABEL_COLOR,
    valAxisLabelFontSize: 8,
    valAxisLabelFormatCode: axisFormatCode(format, maxValue),
    valAxisMaxVal: axisMax,
    valAxisMinVal: 0,
    valGridLine: { color: LINE_COLOR, size: 0.5, style: 'solid' },
  });

  // Overlay the value labels above each bar, positioned from the same value scale as the chart.
  const plotLeft = CHART.x + INSET.left;
  const plotWidth = CHART.w - INSET.left - INSET.right;
  const plotTop = CHART.y + INSET.top;
  const plotBottom = CHART.y + CHART.h - INSET.bottom;
  const plotHeight = plotBottom - plotTop;
  const bandWidth = plotWidth / categories.length;

  categories.forEach((_, index) => {
    const centerX = plotLeft + (index + 0.5) * bandWidth;
    const barTopY = plotBottom - plotHeight * (stackTops[index] / axisMax);
    slide.addText(labels[index] || '', {
      align: 'center',
      bold: true,
      color: TEXT_COLOR,
      fit: 'shrink',
      fontFace: 'Arial',
      fontSize: 8,
      h: 0.18,
      w: bandWidth,
      x: centerX - bandWidth / 2,
      y: barTopY - 0.22,
    });
  });

  addSlideFooter({ pptx, reportDateRange, reportType, reportUrl, shopperAttributes, slide });
  return slide;
};
